import React, {Fragment, useEffect, useState} from "react";
import {WithOrganization} from "../components/organizations/organization-context";
import {useTranslation} from 'react-i18next';
import {OrganizationUnitsBrowser} from "../components/organization-units-browser/OrganizationUnitsBrowser";
import {useNavigate, useParams} from "react-router-dom";
import clsx from "clsx";
import {makeStyles, useMediaQuery} from "@material-ui/core";
import {OrganizationUnitOverview} from "../components/organization-unit-overview/OrganizationUnitOverview";
import {useTheme} from "@material-ui/core/styles";
import {run} from "../utils/function-util";
import {getTopOrganizationUnits} from "../api/organization-unit-functions";
import {OrganizationUnitBrowserSearchListItem} from "../components/organization-units-browser/OrganizationUnitBrowserSearchListItem";
import {ExportMembers} from "../components/members/shared/actions/ExportMembers";

const useStyles = makeStyles(theme => ({
    subscriptionsView: {
        display: 'flex',
        height: 'calc(100vh - 156px)',
        overflowY: 'hidden'
    },
    subscriptionsViewOnMobile: {
        width: '100%',
        overflow: 'scroll'
    },
    organizationUnitsBrowser: {
        display: 'flex',
        width: '25%',
        'border-right': 'thin white solid'
    },
    organizationUnitsBrowserOnMobile: {
        width: '100%',
        '& > *': {
            width: '100vw'
        }
    },
    organizationUnitsOverview: {
        width: '75%',
        display: 'flex',
        flexDirection: 'column'
    },
    organizationUnitsOverviewOnMobile: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
    }
}));

export const Subscriptions: React.FC<WithOrganization> = ({currentOrganization}) => {
    const classes = useStyles();
    const {orgUnitId} = useParams<{ orgUnitId: string }>();
    const [organizationUnitId, setOrganizationUnitId] = useState(parseInt(orgUnitId as string));
    const [exportToCsvOrgUnitId, setExportToCsvOrgUnitId] = useState<number>();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('');
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [displayMobileOverview, setDisplayMobileOverview] = useState({
        displayOverview: false,
        organizationUnitId: -1
    });

    useEffect(() => {
        if (organizationUnitId !== parseInt(orgUnitId as string)) {
            setOrganizationUnitId(parseInt(orgUnitId as string));
        }
    }, [orgUnitId]);

    useEffect(run(async () => (currentOrganization && (!organizationUnitId || isNaN(organizationUnitId))) ?
            await getTopOrganizationUnits(currentOrganization.id) : Promise.resolve([]),
        (tops: any) => {
            if (!organizationUnitId || isNaN(organizationUnitId)) {
                if (tops && tops[0]) {
                    setOrganizationUnitId(tops[0].id);
                } else {
                    navigate(`/organizations`);
                }
            }
        }), [organizationUnitId, currentOrganization]);

    let organizationUnitContextMenuOptions = [
        {key: 'DistributeSubscriptions', text: t('Subscription.DistributeSubscriptions.Title')},
        {key: 'ManageTreeStructure', text: t('OrganizationUnits.Menu.CreateOrgUnit')}
    ];

    if (isMobile) {
        organizationUnitContextMenuOptions = organizationUnitContextMenuOptions.concat([
            {key: 'Overview', text: t('OrganizationUnits.Menu.Overview')},
            {key: 'Explore', text: t('OrganizationUnits.Menu.Explore')},
            {key: 'Export', text: t('Members.ExportCsv')}
        ]);
    }

    const onOrganizationUnitContextMenuOptionClicked = (event: any) => {
        switch (event.key) {
            case 'ManageTreeStructure':
                navigate(`/organization-units/${event.organizationUnitId}/edit`);
                break;
            case 'DistributeSubscriptions' :
                navigate(`/organization-units/${event.organizationUnitId}/subscriptions-distribution`);
                break;
            case 'Overview' :
                setDisplayMobileOverview({displayOverview: true, organizationUnitId: event.organizationUnitId})
                break;
            case 'Explore':
                navigate(`/organization-units/${event.organizationUnitId}/detail?tab=0`);
                break;
            case 'Export':
                setExportToCsvOrgUnitId(event.organizationUnitId)
                break;
            default:
                break;
        }
    };

    const closeOverView = () => {
        setDisplayMobileOverview({displayOverview: false, organizationUnitId: -1});
    }
    const onOrganizationUnitIdChanged = (newOrganizationUnitId: any) => {
        if (newOrganizationUnitId && (newOrganizationUnitId !== organizationUnitId)) {
            setOrganizationUnitId(newOrganizationUnitId);
            navigate(`/organization-units/${newOrganizationUnitId}`);
        }
    };

    const organizationUnitSearch = (<OrganizationUnitBrowserSearchListItem organizationUnitId={organizationUnitId}
                                                                           onFilterChange={setFilter}/>)

    const organizationUnitBrowser = (<div
        className={clsx(classes.organizationUnitsBrowser, isMobile && classes.organizationUnitsBrowserOnMobile)}>
        <OrganizationUnitsBrowser
            organizationUnitId={organizationUnitId}
            onOrganizationUnitIdChanged={onOrganizationUnitIdChanged}
            organizationUnitContextMenuOptions={organizationUnitContextMenuOptions}
            onOrganizationUnitContextMenuOptionClicked={onOrganizationUnitContextMenuOptionClicked}
            organizationUnitSearch={organizationUnitSearch}
            filter={filter}
        />
    </div>)

    const mobileOrganizationOverview = (<div className={clsx(classes.organizationUnitsOverviewOnMobile)}>
        <OrganizationUnitOverview
            organizationUnitId={displayMobileOverview.organizationUnitId}
            filter={filter}
            onCloseOverview={closeOverView}/>
    </div>)

    const desktopOrganizationOverview = (<div className={clsx(classes.organizationUnitsOverview)}>
        <OrganizationUnitOverview
            organizationUnitId={organizationUnitId}
            filter={filter}/>
    </div>)

    return (
        <div className={clsx(classes.subscriptionsView, isMobile && classes.subscriptionsViewOnMobile)}>
            {!isNaN(organizationUnitId) &&
            <React.Fragment>
                {
                    exportToCsvOrgUnitId &&
                    <ExportMembers
                        organizationUnitId={exportToCsvOrgUnitId}
                        onError={() => setExportToCsvOrgUnitId(undefined)}
                        onDone={() => setExportToCsvOrgUnitId(undefined)}
                    />
                }
                {isMobile && displayMobileOverview.displayOverview ? mobileOrganizationOverview :
                    <Fragment>
                        {organizationUnitBrowser}
                        {!isMobile && desktopOrganizationOverview}
                    </Fragment>
                }
            </React.Fragment>
            }
        </div>
    )
};
